import type {
  FormDetail,
  FormSummary,
  HistoryEntry,
  Intake,
  PageInfo,
  PaymentTag,
  UpdateContact,
} from '@atlas-control/shared';
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface FormFilters {
  payment?: PaymentTag;
  reviewed?: 'yes' | 'no';
  hasRun?: 'yes' | 'no';
  q?: string;
}

export const formsQuery = (filters: FormFilters, page: number) =>
  queryOptions({
    queryKey: ['forms', 'list', filters, page],
    queryFn: () =>
      api<{ forms: FormSummary[] } & PageInfo>('/forms', { query: { ...filters, page } }),
    placeholderData: keepPreviousData,
  });

export const formQuery = (id: string) =>
  queryOptions({
    queryKey: ['forms', 'detail', id],
    queryFn: () => api<{ form: FormDetail }>(`/forms/${id}`).then((r) => r.form),
  });

export const historyQuery = (entityType: 'form' | 'run', entityId: string, page: number) =>
  queryOptions({
    queryKey: ['history', entityType, entityId, page],
    queryFn: () =>
      api<{ history: HistoryEntry[] } & PageInfo>(`/history/${entityType}/${entityId}`, {
        query: { page },
      }),
    placeholderData: keepPreviousData,
  });

/** Mutations for one form. Each success replaces the cached detail and refreshes lists and history. */
export function useFormMutations(id: string) {
  const queryClient = useQueryClient();
  const onSuccess = (res: { form: FormDetail }) => {
    queryClient.setQueryData(formQuery(id).queryKey, res.form);
    void queryClient.invalidateQueries({ queryKey: ['forms', 'list'] });
    void queryClient.invalidateQueries({ queryKey: ['history', 'form', id] });
  };
  const path = `/forms/${id}`;

  return {
    updateIntake: useMutation({
      mutationFn: (payload: Intake) =>
        api<{ form: FormDetail }>(`${path}/intake`, { method: 'PUT', body: { payload } }),
      onSuccess,
    }),
    updateContact: useMutation({
      mutationFn: (patch: UpdateContact) =>
        api<{ form: FormDetail }>(`${path}/contact`, { method: 'PATCH', body: patch }),
      onSuccess,
    }),
    setPayment: useMutation({
      mutationFn: (tag: PaymentTag) =>
        api<{ form: FormDetail }>(`${path}/payment`, { method: 'PUT', body: { tag } }),
      onSuccess,
    }),
    review: useMutation({
      mutationFn: (notes: string | undefined) =>
        api<{ form: FormDetail }>(`${path}/review`, { method: 'POST', body: { notes } }),
      onSuccess,
    }),
    undoReview: useMutation({
      mutationFn: () => api<{ form: FormDetail }>(`${path}/review`, { method: 'DELETE' }),
      onSuccess,
    }),
    remove: useMutation({
      mutationFn: () => api<undefined>(path, { method: 'DELETE' }),
      onSuccess: () => {
        queryClient.removeQueries({ queryKey: formQuery(id).queryKey });
        void queryClient.invalidateQueries({ queryKey: ['forms', 'list'] });
        void queryClient.invalidateQueries({ queryKey: ['runs'] });
      },
    }),
  };
}
